import {
  booleanAttr,
  IMAGE_LAYOUT_CLASSES,
  numberAttr,
  rawAttr,
  stringAttr,
  type ImageLayoutClass,
} from "@wiki/core";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import type { EditorView, NodeView } from "prosemirror-view";

import type { EditorContext } from "../context";
import { embedSource } from "../commands/media";
import { pickFiles, uploadFiles } from "../plugins/upload";

/** An image below this size renders as an inline icon, with no controls (§5). */
export const ICON_IMAGE_SIZE = 32;

function button(label: string, title: string, onClick: () => void): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button";
  element.className = "wiki-node-toolbar__button";
  element.textContent = label;
  element.title = title;
  element.setAttribute("aria-label", title);
  element.addEventListener("click", (event) => {
    event.preventDefault();
    onClick();
  });
  return element;
}

export function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(size) / Math.log(1024)));
  const value = size / 1024 ** exponent;
  return `${exponent === 0 ? String(size) : value.toFixed(1)} ${units[exponent] ?? "B"}`;
}

export function imageLayoutClass(node: ProseMirrorNode): string {
  const layout = rawAttr(node, "layoutClass");
  const known = IMAGE_LAYOUT_CLASSES.find((entry) => entry === layout);
  return known === undefined ? "wiki-image--center" : `wiki-image--${known}`;
}

/**
 * The image node view: alignment, caption (the `alt` attribute, edited in
 * place), dimension inputs, aspect-preserving drag resize, download, replace,
 * and a lightbox on click in read mode (`editor.md` §5).
 */
export class ImageView implements NodeView {
  readonly dom: HTMLElement;
  private node: ProseMirrorNode;
  private readonly image: HTMLImageElement;
  private readonly caption: HTMLElement;
  private readonly toolbar: HTMLElement;

  constructor(
    node: ProseMirrorNode,
    private readonly view: EditorView,
    private readonly getPos: () => number | undefined,
    private readonly context: EditorContext,
  ) {
    this.node = node;
    this.dom = document.createElement("figure");
    this.image = document.createElement("img");
    this.caption = document.createElement("figcaption");
    this.toolbar = document.createElement("div");
    this.toolbar.className = "wiki-node-toolbar";
    this.dom.append(this.image, this.caption, this.toolbar);
    this.image.addEventListener("click", () => {
      if (!this.context.readOnly) return;
      this.context.onOpenLightbox(stringAttr(this.node, "src") ?? "", this.alt());
    });
    this.caption.addEventListener("input", () => {
      this.setAttrs({ alt: this.caption.textContent ?? "" });
    });
    this.render();
  }

  private alt(): string | null {
    const alt = rawAttr(this.node, "alt");
    return typeof alt === "string" ? alt : null;
  }

  private isIcon(): boolean {
    const width = numberAttr(this.node, "width") ?? 0;
    const height = numberAttr(this.node, "height") ?? 0;
    return width > 0 && height > 0 && width < ICON_IMAGE_SIZE && height < ICON_IMAGE_SIZE;
  }

  private setAttrs(attrs: Record<string, unknown>): void {
    const pos = this.getPos();
    if (pos === undefined) return;
    const tr = this.view.state.tr.setNodeMarkup(pos, undefined, { ...this.node.attrs, ...attrs });
    this.view.dispatch(tr);
  }

  private render(): void {
    const src = stringAttr(this.node, "src") ?? "";
    const width = rawAttr(this.node, "width");
    const height = rawAttr(this.node, "height");
    this.dom.className = [
      "wiki-image",
      imageLayoutClass(this.node),
      this.isIcon() ? "wiki-image--icon" : "",
    ]
      .filter((entry) => entry !== "")
      .join(" ");
    this.image.src = src;
    this.image.alt = this.alt() ?? "";
    if (typeof width === "number") this.image.setAttribute("width", String(width));
    else this.image.removeAttribute("width");
    if (typeof height === "number") this.image.setAttribute("height", String(height));
    else this.image.removeAttribute("height");

    this.caption.className = "wiki-image__caption";
    if (this.caption.textContent !== (this.alt() ?? "")) {
      this.caption.textContent = this.alt() ?? "";
    }
    const editable = !this.context.readOnly && !this.isIcon();
    this.caption.setAttribute("contenteditable", editable ? "true" : "false");
    this.caption.setAttribute("data-placeholder", "Write a caption");

    this.toolbar.replaceChildren();
    if (!editable) return;
    this.toolbar.append(
      ...this.alignmentButtons(),
      this.dimensionInput("width"),
      this.dimensionInput("height"),
      button("⭳", "Download image", () => {
        this.context.openLink(stringAttr(this.node, "src") ?? "");
      }),
      button("Replace", "Replace image", () => {
        void pickFiles("image/*").then(async (files) => {
          const pos = this.getPos();
          if (files.length === 0 || pos === undefined) return;
          this.view.dispatch(this.view.state.tr.delete(pos, pos + this.node.nodeSize));
          await uploadFiles(this.view, files, pos, this.context, "image");
        });
      }),
      button("Delete", "Delete image", () => {
        const pos = this.getPos();
        if (pos === undefined) return;
        this.view.dispatch(this.view.state.tr.delete(pos, pos + this.node.nodeSize));
      }),
    );
    this.toolbar.append(this.resizeHandle());
  }

  private alignmentButtons(): HTMLButtonElement[] {
    const options: { label: string; value: ImageLayoutClass | null }[] = [
      { label: "Align left", value: "left-50" },
      { label: "Align center", value: null },
      { label: "Align right", value: "right-50" },
      { label: "Full width", value: "full-width" },
    ];
    return options.map((option) => {
      const element = button(option.label, option.label, () => {
        this.setAttrs({ layoutClass: option.value });
      });
      element.setAttribute("data-alignment", option.value ?? "center");
      if ((rawAttr(this.node, "layoutClass") ?? null) === option.value) {
        element.setAttribute("aria-pressed", "true");
      }
      return element;
    });
  }

  private dimensionInput(dimension: "width" | "height"): HTMLInputElement {
    const input = document.createElement("input");
    input.type = "number";
    input.className = "wiki-node-toolbar__input";
    input.setAttribute("aria-label", `Image ${dimension}`);
    const value = numberAttr(this.node, dimension);
    input.value = value === null ? "" : String(value);
    input.addEventListener("change", () => {
      const parsed = Number(input.value);
      this.setAttrs({ [dimension]: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined });
    });
    return input;
  }

  /** Dragging preserves the aspect ratio and writes both dimensions. */
  private resizeHandle(): HTMLElement {
    const handle = document.createElement("span");
    handle.className = "wiki-image__resize";
    handle.setAttribute("data-resize-handle", "true");
    handle.addEventListener("mousedown", (event: MouseEvent) => {
      event.preventDefault();
      const startX = event.clientX;
      const rect = this.image.getBoundingClientRect();
      const startWidth = Number(rawAttr(this.node, "width") ?? rect.width) || rect.width || 1;
      const startHeight = Number(rawAttr(this.node, "height") ?? rect.height) || rect.height || 1;
      const ratio = startHeight / startWidth;
      const move = (moveEvent: MouseEvent): void => {
        const width = Math.max(
          ICON_IMAGE_SIZE,
          Math.round(startWidth + (moveEvent.clientX - startX)),
        );
        this.setAttrs({ width, height: Math.round(width * ratio) });
      };
      const up = (): void => {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    });
    return handle;
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  selectNode(): void {
    this.dom.classList.add("ProseMirror-selectednode");
  }

  deselectNode(): void {
    this.dom.classList.remove("ProseMirror-selectednode");
  }

  stopEvent(event: Event): boolean {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return false;
    return this.toolbar.contains(target) || this.caption.contains(target);
  }

  ignoreMutation(): boolean {
    return true;
  }
}

export class VideoView implements NodeView {
  readonly dom: HTMLElement;

  constructor(node: ProseMirrorNode) {
    this.dom = document.createElement("div");
    this.dom.className = "wiki-video";
    const video = document.createElement("video");
    video.controls = true;
    video.src = stringAttr(node, "src") ?? "";
    const width = rawAttr(node, "width");
    const height = rawAttr(node, "height");
    if (typeof width === "number") video.width = width;
    if (typeof height === "number") video.height = height;
    const title = rawAttr(node, "title");
    if (typeof title === "string" && title !== "") video.title = title;
    this.dom.append(video);
  }

  stopEvent(): boolean {
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }
}

/**
 * The attachment card and its toolbar (`editor.md` §5, Attachment): replace,
 * delete, a preview toggle that renders a PDF inline, preview dimensions, and
 * download.
 */
export class AttachmentView implements NodeView {
  readonly dom: HTMLElement;
  private node: ProseMirrorNode;
  private readonly card: HTMLElement;
  private readonly toolbar: HTMLElement;

  constructor(
    node: ProseMirrorNode,
    private readonly view: EditorView,
    private readonly getPos: () => number | undefined,
    private readonly context: EditorContext,
  ) {
    this.node = node;
    this.dom = document.createElement("div");
    this.dom.className = "wiki-attachment";
    this.card = document.createElement("div");
    this.card.className = "wiki-attachment__card";
    this.toolbar = document.createElement("div");
    this.toolbar.className = "wiki-node-toolbar";
    this.dom.append(this.card, this.toolbar);
    this.render();
  }

  private href(): string {
    return stringAttr(this.node, "href") ?? "";
  }

  private setAttrs(attrs: Record<string, unknown>): void {
    const pos = this.getPos();
    if (pos === undefined) return;
    this.view.dispatch(
      this.view.state.tr.setNodeMarkup(pos, undefined, { ...this.node.attrs, ...attrs }),
    );
  }

  private render(): void {
    this.dom.setAttribute("data-content-type", stringAttr(this.node, "contentType") ?? "");
    this.card.replaceChildren();
    const link = document.createElement("a");
    link.className = "wiki-attachment__link";
    link.href = this.href();
    link.textContent = stringAttr(this.node, "title") ?? "Attachment";
    link.addEventListener("click", (event) => {
      event.preventDefault();
      this.context.openLink(this.href());
    });
    const size = document.createElement("span");
    size.className = "wiki-attachment__size";
    size.textContent = formatBytes(numberAttr(this.node, "size") ?? 0);
    this.card.append(link, size);

    const preview = booleanAttr(this.node, "preview");
    if (preview) {
      const frame = document.createElement("iframe");
      frame.className = "wiki-attachment__preview";
      frame.src = this.href();
      frame.title = stringAttr(this.node, "title") ?? "Attachment preview";
      const width = numberAttr(this.node, "width");
      const height = numberAttr(this.node, "height");
      if (width !== null) frame.width = String(width);
      if (height !== null) frame.height = String(height);
      this.card.append(frame);
    }

    this.toolbar.replaceChildren();
    if (this.context.readOnly) {
      this.toolbar.append(
        button("⭳", "Download file", () => {
          this.context.openLink(this.href());
        }),
      );
      return;
    }
    this.toolbar.append(
      button("Replace", "Replace file", () => {
        void pickFiles("*/*").then(async (files) => {
          const pos = this.getPos();
          if (files.length === 0 || pos === undefined) return;
          this.view.dispatch(this.view.state.tr.delete(pos, pos + this.node.nodeSize));
          await uploadFiles(this.view, files, pos, this.context, "attachment");
        });
      }),
      button("Delete", "Delete file", () => {
        const pos = this.getPos();
        if (pos === undefined) return;
        this.view.dispatch(this.view.state.tr.delete(pos, pos + this.node.nodeSize));
      }),
      button(preview ? "Hide preview" : "Show preview", "Toggle preview", () => {
        this.setAttrs({ preview: !preview });
      }),
      this.dimensionInput("width"),
      this.dimensionInput("height"),
      button("⭳", "Download file", () => {
        this.context.openLink(this.href());
      }),
    );
  }

  private dimensionInput(dimension: "width" | "height"): HTMLInputElement {
    const input = document.createElement("input");
    input.type = "number";
    input.className = "wiki-node-toolbar__input";
    input.setAttribute("aria-label", `Preview ${dimension}`);
    const value = numberAttr(this.node, dimension);
    input.value = value === null ? "" : String(value);
    input.addEventListener("change", () => {
      const parsed = Number(input.value);
      this.setAttrs({ [dimension]: Number.isFinite(parsed) && parsed > 0 ? parsed : null });
    });
    return input;
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  stopEvent(): boolean {
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }
}

/**
 * Embeds frame third-party pages, so only https URLs on the allowlist are
 * framed; anything else renders as a plain link (`commands/media.ts`).
 */
export class EmbedView implements NodeView {
  readonly dom: HTMLElement;

  constructor(node: ProseMirrorNode, context: EditorContext) {
    const href = stringAttr(node, "href") ?? "";
    const source = embedSource(href);
    this.dom = document.createElement("div");
    this.dom.className = "wiki-embed";
    if (source === null) {
      this.dom.classList.add("wiki-embed--blocked");
      const link = document.createElement("a");
      link.href = href;
      link.textContent = href;
      link.addEventListener("click", (event) => {
        event.preventDefault();
        context.openLink(href);
      });
      this.dom.append(link);
      return;
    }
    const frame = document.createElement("iframe");
    frame.src = source;
    frame.title = href;
    frame.setAttribute("allowfullscreen", "true");
    frame.setAttribute("referrerpolicy", "no-referrer");
    frame.setAttribute("sandbox", "allow-scripts allow-same-origin allow-popups allow-forms");
    this.dom.append(frame);
  }

  stopEvent(): boolean {
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }
}
